import { useMemo } from 'react';
import { PAGE_TYPE } from '../types/pages.type';
import { getPercentageByTotal } from '../utilis/common';
import useAICalculationContext from './useAICalculationContext';
import useAICardDataContext from './useAICardDataContext';
import useAIFacilityContext from './useAIFacilityContext';
import useAIChartAndPatientContext from './useAIChartAndPatientContext';
import { generateChartAnalysis } from '../utilis/chart-analytics';

/**
 * Custom hook for AI Chat Context specific to ADMISSION page
 * Handles Admission-specific filtering logic and card data
 */
export const useAIAdmissionChatContext = ({
  // Core data dependencies
  dbData,
  filterListData,
  patientList,
  activeFacilities,
  facilities,
  dynamicCards,
  
  // Filter and state dependencies
  filter,
  transferType, // ADMISSION or READMISSION
  cardFilter,
  percentageBy,
  lockedTotalBy,
  specialComparisonSet,
  filterTotal,
  percentageAgainst,
  projectionDays,
  lastADTDate,
  
  // UI state dependencies - ADMISSION SPECIFIC
  admissionCards,
  rangesSet,
  isLayoutMode,
  
  // Function dependencies
  getPriorityNumber,
  lockedTotal,
  lockedByFacility,
  isCensusTotalLocked,
  facilityPercentage,
  
  // Metadata dependencies - ADMISSION SPECIFIC
  ADMISSION_CUSTOM_CARD_DATA,
  tooltipsInfo,
  
  // Service dependencies
  aiCensusActionsService,
  aiReportActionsService,
  aiTransferActionsService,
  aiDialogActionsService,
  aiDateSuggestionsService,
  
  // Chart data dependencies
  chartData = null,
  chartAggregationType = 'unknown',
  currentChartFilter = null,
  
  // Admission-specific dependencies
  lockFilterTotalState = null,
  reversedColorAdmission = false,
  comparingAgainstDifferentFacility = false
}) => {
  // Use sub-hooks for different aspects of the context
  const calculationContext = useAICalculationContext({
    filter,
    transferType,
    projectionDays,
    cardFilter,
    percentageBy,
    lockedTotalBy,
    specialComparisonSet,
    filterTotal,
    percentageAgainst
  });

  const cardDataContext = useAICardDataContext({
    pageType: PAGE_TYPE.ADMISSION,
    filterListData,
    filter,
    transferType,
    projectionDays,
    cardFilter,
    percentageBy,
    lockedTotalBy,
    specialComparisonSet,
    filterTotal,
    percentageAgainst,
    dbData,
    getPriorityNumber,
    dynamicCards,
    patientList,
    ADMISSION_CUSTOM_CARD_DATA
  });

  const facilityContext = useAIFacilityContext({
    activeFacilities,
    patientList,
    facilities,
    dbData,
    percentageBy,
    lockedTotal,
    lockedTotalBy,
    pageType: PAGE_TYPE.ADMISSION
  });

  const chartAndPatientContext = useAIChartAndPatientContext();

  return useMemo(() => {
    // Calculate enhanced metrics for AI analysis (ADMISSION PAGE SPECIFIC)
    const totalAdmissions = dbData?.total || 0; // Total admissions (new + readmissions)
    const admissionCount = dbData?.totalAdmissions || 0; // New admissions only
    const readmissionCount = dbData?.totalReAdmissions || 0; // Readmissions only
    
    // Get active filters count
    const activeFiltersCount = cardFilter ? Object.values(cardFilter).filter(arr => Array.isArray(arr) && arr.length > 0).length : 0;
    
    // Calculate data availability
    const dataCategories = filterListData ? Object.keys(filterListData).filter(key => 
      Array.isArray(filterListData[key]) && filterListData[key].length > 0
    ) : [];

    // Get the correct percentage base (same logic as cards use)
    // CRITICAL: Pass PAGE_TYPE.ADMISSION to ensure bed capacity is used as default baseline
    const currentPercentageBase = specialComparisonSet ? filterTotal : getPercentageByTotal(dbData, PAGE_TYPE.ADMISSION);

    // ADMISSION-SPECIFIC: Terminology definitions for AI
    const terminology = {
      admission: {
        label: "Admission",
        meaning: "New patient admissions to the facility",
        description: "First-time admissions of patients to the facility. These are patients being admitted for the first time in the tracking period.",
        keyTerms: ["admission", "admissions", "new admission", "first admission", "admitted"]
      },
      readmission: {
        label: "Readmission",
        meaning: "Patients returning to the facility after discharge",
        description: "Patients who were previously discharged and are being readmitted. Readmissions are important for quality metrics and care continuity tracking.",
        keyTerms: ["readmission", "readmissions", "returning patient", "re-admitted", "readmit"]
      },
      important: "When users ask about 'admissions', clarify whether they mean total admissions (admission + readmission) or just new admissions."
    };

    // ADMISSION-SPECIFIC: Percentage Calculation Context
    const percentageContext = {
      currentMethod: percentageBy,
      currentPercentageBase,
      percentageAgainst,
      filterTotal,
      specialComparisonSet,
      isComparingAgainstAvgCensus: filterListData?.isComparingAgainstAvgCensus,
      lockedTotalBy,
      transferType,
      bedCapacity: dbData?.bedCapacity,
      calculation: {
        description: `Percentages calculated using ${percentageBy} method`,
        baseValue: currentPercentageBase,
        explanation: specialComparisonSet 
          ? `Using filterTotal (${filterTotal}) as base because specialComparisonSet is true`
          : `Using percentageAgainst (${percentageAgainst}) as base from ${percentageBy} method`,
        admissionSpecific: `Admission page compares against bed capacity (${dbData?.bedCapacity}) when locked`
      },
      // ADMISSION-SPECIFIC: Lock Filter Total State
      lockFilterTotalState: lockFilterTotalState ? {
        isLocked: lockFilterTotalState.isLocked,
        lockedFilterTotal: lockFilterTotalState.lockedFilterTotal,
        explanation: lockFilterTotalState.isLocked 
          ? `Percentages locked to ${lockFilterTotalState.lockedFilterTotal}`
          : 'No lock applied, using bed capacity as comparison base'
      } : null,
      // Priority Filter System Context (explains percentage base changes)
      priorityFiltering: {
        activePriorityFilters: cardFilter?.priorityData || [],
        totalActivePriorityFilters: cardFilter?.priorityData?.length || 0,
        firstPriorityCard: cardFilter?.priorityData?.[0]?.type || null,
        explanation: `When priority filters are applied on Admission page, percentage bases change:
          - First priority filter uses bed capacity or locked total as base
          - Subsequent filters use filtered patient count as denominator
          - ADT filters can be applied after admission filters in priority chain
          - Example: If "Per Doctor" filter is applied first, percentages for other cards use filtered doctor admissions as base`,
        scalingLogic: `Percentages are scaled when:
          - isComparingAgainstAvgCensus is true
          - lockedTotalBy === "census"
          - First priority item AND no transfer type filter applied`,
        admissionFilterTypes: {
          ADMISSION: 'Regular admission card filters (doctor, days, dx, insurance, floors, hospital)',
          ADT: 'ADT-related filters applied after admission filters'
        }
      }
    };

    // Census Percentage Settings for AI interaction
    const censusSettings = (() => {
      try {
        const settings = aiCensusActionsService?.getCurrentPercentageSettings();
        return {
          currentMethod: settings?.currentMethod,
          customLabel: settings?.customLabel,
          customPercentages: settings?.customPercentages,
          availableMethods: settings?.availableMethods,
          censusData: settings?.censusData,
          explanations: {
            bedCapacity: aiCensusActionsService?.getPercentageExplanation('Bed Capacity'),
            censusAsOfNow: aiCensusActionsService?.getPercentageExplanation('census as of now'),
            censusAverage: aiCensusActionsService?.getPercentageExplanation('Average census of time frame selected')
          },
          actionSuggestions: aiCensusActionsService?.getAIActionSuggestions()
        };
      } catch (error) {
        console.warn('AI Census Actions Service not initialized:', error);
        return {
          currentMethod: percentageBy,
          available: false,
          error: 'Service not initialized'
        };
      }
    })();

    // Current state
    const currentState = {
      selectedTransferType: transferType, // ADMISSION or READMISSION or null (all)
      transferTypeLabel: transferType === 'ADMISSION' ? 'New Admissions' : transferType === 'READMISSION' ? 'Readmissions' : 'All Admissions',
      dateRange: filter?.startDate && filter?.endDate 
        ? `${filter.startDate} to ${filter.endDate}` 
        : 'Not set',
      activeFiltersCount,
      availableDataCategories: dataCategories,
      selectedCards: admissionCards?.length || 0,
      totalCards: admissionCards?.length || 0,
      hasComparison: !!rangesSet,
      isLayoutMode,
      facilityInfo: {
        hasMultipleFacilities: activeFacilities?.length > 1,
        facilityCount: activeFacilities?.length || 0,
        activeFacilities: activeFacilities
      },
      admissionSpecific: {
        reversedColorAdmission,
        comparingAgainstDifferentFacility,
        lockFilterTotalState: lockFilterTotalState ? {
          isLocked: lockFilterTotalState.isLocked,
          count: lockFilterTotalState.count,
          lockedFilterTotal: lockFilterTotalState.lockedFilterTotal
        } : { isLocked: false, count: 0, lockedFilterTotal: null }
      }
    };

    // Filter information
    const filters = {
      dateRange: {
        startDate: filter?.startDate,
        endDate: filter?.endDate,
        isCustomRange: filter?.startDate && filter?.endDate
      },
      facilities: {
        active: activeFacilities,
        count: activeFacilities?.length || 0
      },
      cardFilters: cardFilter,
      priorityFilters: cardFilter?.priorityData || [],
      // ADMISSION-SPECIFIC: ADT filters
      adtFilters: cardFilter?.adtAdmitPatientIds || [],
      adtData: cardFilter?.adtData || [],
      hasAdtFilters: (cardFilter?.adtAdmitPatientIds?.length || 0) > 0,
      // ADMISSION-SPECIFIC: Filter type breakdown
      admissionFilters: cardFilter?.priorityData?.filter(f => f.filterType === 'ADMISSION') || [],
      adtPriorityFilters: cardFilter?.priorityData?.filter(f => f.filterType === 'ADT') || []
    };

    // Data structure for AI analysis
    const dataStructure = {
      dbData: {
        available: !!dbData,
        recordCount: totalAdmissions,
        hasAdmissionData: admissionCount > 0,
        hasReadmissionData: readmissionCount > 0,
        bedCapacity: dbData?.bedCapacity,
        censusAverage: dbData?.censusAverage,
        // ADMISSION-SPECIFIC: Breakdown by type
        breakdownByType: {
          total: totalAdmissions,
          newAdmissions: admissionCount,
          readmissions: readmissionCount,
          readmissionRate: totalAdmissions > 0 ? ((readmissionCount / totalAdmissions) * 100).toFixed(1) : 0
        }
      },
      filterListData: {
        available: !!filterListData,
        categories: dataCategories,
        totalCategories: dataCategories.length,
        // ADMISSION-SPECIFIC: All 6 main cards
        admissionCards: {
          doctorData: filterListData?.doctorData?.length || 0,
          daysData: filterListData?.daysData?.length || 0,
          dxData: filterListData?.dxData?.length || 0,
          insuranceData: filterListData?.insuranceData?.length || 0,
          floorsData: filterListData?.floorsData?.length || 0,
          hospitalData: filterListData?.hospitalData?.length || 0
        },
        // ADMISSION-SPECIFIC: Additional metadata
        admissionTotal: filterListData?.admissionTotal || 0,
        isComparingAgainstAvgCensus: filterListData?.isComparingAgainstAvgCensus || false,
        lockedTotalBy: filterListData?.lockedTotalBy || null
      },
      patientList: {
        available: !!patientList?.list,
        count: patientList?.list?.length || 0,
        // ADMISSION-SPECIFIC: Type breakdown
        typeBreakdown: {
          admissions: patientList?.list?.filter(p => p.type === 'ADMISSION')?.length || 0,
          readmissions: patientList?.list?.filter(p => p.type === 'READMISSION')?.length || 0
        },
        hasAdtList: !!(patientList?.adtList?.length),
        adtListCount: patientList?.adtList?.length || 0,
        hasCustomTabs: !!(patientList?.customTabs?.length),
        customTabsCount: patientList?.customTabs?.length || 0,
        hasDynamicCards: !!(dynamicCards?.length),
        dynamicCardsCount: dynamicCards?.length || 0,
        // ADMISSION-SPECIFIC: Additional patient list data
        diffDashboardPatients: patientList?.diffDashboardPatients?.length || 0,
        customCombineTabData: patientList?.customCombineTabData?.length || 0
      }
    };

    // Available actions for AI suggestions - ADMISSION SPECIFIC
    const availableActions = {
      // Transfer Type Actions (Admission vs Readmission)
      canChangeTransferType: true,
      canShowAdmissions: true,
      canShowReadmissions: true,
      canShowAllAdmissions: true,
      canClearTransferFilter: true,
      
      // Filter Actions
      canApplyFilters: dataCategories.length > 0,
      canApplyCardFilters: true,
      canApplyMultipleFilters: true,
      canToggleSpecificItems: true,
      canResetFilters: activeFiltersCount > 0,
      canToggleAllFilters: true,
      canClearSpecificFilters: true,
      
      // Admission-Specific Filter Actions
      canFilterByDoctor: filterListData?.doctorData?.length > 0,
      canFilterByDay: filterListData?.daysData?.length > 0,
      canFilterByDiagnosis: filterListData?.dxData?.length > 0,
      canFilterByInsurance: filterListData?.insuranceData?.length > 0,
      canFilterByFloor: filterListData?.floorsData?.length > 0,
      canFilterByHospital: filterListData?.hospitalData?.length > 0,
      
      // Report Generation Actions
      canGenerateReports: totalAdmissions > 0,
      canGeneratePdfReports: totalAdmissions > 0,
      canGenerateExcelReports: totalAdmissions > 0,
      canSetupAutomatedReports: totalAdmissions > 0,
      canGenerateCardSpecificReports: dataCategories.length > 0,
      canGenerateFilteredReports: activeFiltersCount > 0,
      
      // Date and Time Actions
      canChangeDateRange: true,
      canSuggestDateRanges: true,
      canApplyDateFilters: true,
      
      // Percentage and Census Actions
      canChangePercentageMethod: true,
      canAddCustomPercentage: true,
      canExplainPercentageCalculations: true,
      canChangeCensusMethod: true,
      canLockTotals: true,
      canUnlockTotals: true,
      canCompareToBedCapacity: !!dbData?.bedCapacity,
      
      // Layout and Display Actions
      canToggleCards: admissionCards?.length > 0,
      canChangeLayout: true,
      canCompareData: true,
      canAnalyzeData: totalAdmissions > 0,
      canReverseColors: true,
      
      // Chart and Details Actions
      canViewChartData: totalAdmissions > 0,
      canAnalyzeChartTrends: totalAdmissions > 0,
      canViewPatientDetails: totalAdmissions > 0,
      canAnalyzeAdmissionPatterns: totalAdmissions > 0,
      canCompareTimePeriods: totalAdmissions > 0,
      canViewDailyTrends: totalAdmissions > 0,
      canViewWeeklyTrends: totalAdmissions > 0,
      canViewMonthlyTrends: totalAdmissions > 0,
      
      // Advanced Actions
      canExplainDataRelationships: true,
      canProvideInsights: totalAdmissions > 0,
      canSuggestOptimalFilters: dataCategories.length > 0,
      canExplainFilterImpact: activeFiltersCount > 0,
      canAnalyzeReadmissionRates: readmissionCount > 0
    };

    // Report Actions Context
    const reportActions = (() => {
      try {
        const reportStatus = aiReportActionsService?.getCurrentReportStatus();
        const reportOptions = aiReportActionsService?.getAvailableReportOptions();
        return {
          currentStatus: reportStatus,
          availableCards: reportStatus?.availableCards,
          canGenerateReports: reportStatus?.canGenerateReports,
          reportTypes: reportStatus?.reportTypes,
          automatedTypes: reportStatus?.automatedTypes,
          suggestions: aiReportActionsService?.getAIActionSuggestions(),
          options: reportOptions
        };
      } catch (error) {
        console.warn('AI Report Actions Service not initialized:', error);
        return {
          available: false,
          error: 'Service not initialized'
        };
      }
    })();

    // Transfer Actions Context (Admission-specific)
    const transferActions = (() => {
      try {
        const transferStatus = aiTransferActionsService?.getCurrentTransferStatus();
        const availableTypes = aiTransferActionsService?.getAvailableTransferTypes();
        return {
          currentStatus: transferStatus,
          availableTypes: ['admission', 'readmission', 'all'],
          currentType: transferStatus?.currentType || transferType,
          currentLabel: transferStatus?.currentLabel,
          currentCount: transferStatus?.currentCount,
          totalAdmissions: admissionCount,
          totalReadmissions: readmissionCount,
          totalAll: totalAdmissions,
          percentages: {
            admissions: totalAdmissions > 0 ? ((admissionCount / totalAdmissions) * 100).toFixed(1) : 0,
            readmissions: totalAdmissions > 0 ? ((readmissionCount / totalAdmissions) * 100).toFixed(1) : 0
          },
          suggestions: aiTransferActionsService?.getAIActionSuggestions(),
          typeDetails: availableTypes
        };
      } catch (error) {
        console.warn('AI Transfer Actions Service not initialized:', error);
        return {
          available: false,
          error: 'Service not initialized'
        };
      }
    })();

    // Dialog Actions Context
    const dialogActions = (() => {
      try {
        const dialogStatus = aiDialogActionsService?.getCurrentDialogStatus();
        return {
          available: dialogStatus?.initialized,
          canOpenChartDialogs: dialogStatus?.canOpenChartDialogs,
          canOpenPatientListDialogs: true,
          availableActions: dialogStatus?.availableActions,
          suggestions: [
            {
              type: 'chart_view',
              description: 'Open chart view for admission trend analysis',
              triggers: ['chart', 'graph', 'trend', 'over time', 'admission trend']
            },
            {
              type: 'patient_list',
              description: 'Show patient/admission list for detailed breakdown',
              triggers: ['patients', 'list', 'who are', 'breakdown', 'admissions']
            },
            {
              type: 'doctor_breakdown',
              description: 'Show admissions by doctor',
              triggers: ['doctors', 'per doctor', 'by doctor']
            },
            {
              type: 'diagnosis_breakdown',
              description: 'Show admissions by diagnosis',
              triggers: ['diagnosis', 'dx', 'per diagnosis']
            }
          ]
        };
      } catch (error) {
        console.warn('AI Dialog Actions Service not initialized:', error);
        return {
          available: false,
          error: 'Service not initialized'
        };
      }
    })();

    // Locked Functionality Context
    const lockedFunctionality = {
      currentState: {
        lockedTotal,
        lockedByFacility,
        lockedTotalBy,
        isCensusTotalLocked,
        facilityPercentage,
        lockFilterTotalState
      },
      isLocked: !!lockedTotal || !!lockedByFacility?.length || lockFilterTotalState?.isLocked,
      lockType: lockedTotalBy,
      lockDescription: lockedTotalBy === 'census' 
        ? 'Percentages locked to census data'
        : lockedTotalBy 
          ? `Percentages locked to ${lockedTotalBy}`
          : lockFilterTotalState?.isLocked
            ? `Percentages locked to ${lockFilterTotalState.lockedFilterTotal}`
            : 'No percentage lock applied',
      canLock: true,
      canUnlock: !!lockedTotal || !!lockedByFacility?.length,
      lockOptions: {
        byCensus: {
          type: 'census',
          description: 'Lock percentages to census data',
          available: true
        },
        byBedCapacity: {
          type: 'bedCapacity',
          description: 'Lock percentages to bed capacity',
          available: !!dbData?.bedCapacity
        },
        byTransferType: {
          type: transferType,
          description: `Lock percentages to ${transferType || 'current'} type`,
          available: !!transferType
        },
        byFacility: {
          type: 'facility',
          description: 'Lock percentages by facility',
          available: activeFacilities?.length > 1
        }
      },
      explanation: `Locked functionality allows fixing percentage calculations to specific baselines:
        - Census Lock: Uses census data as percentage base
        - Bed Capacity Lock: Uses facility bed capacity as base (Admission-specific)
        - Transfer Type Lock: Uses specific type (admission/readmission) as base
        - Facility Lock: Calculates percentages per facility
        - Priority Filter Lock: First applied filter becomes the percentage base`
    };

    // Date Filtering Context
    const dateFiltering = {
      currentRange: filter,
      hasDateConstraints: false,
      canSuggestDates: true,
      validationInfo: aiDateSuggestionsService?.getCurrentValidationInfo()
    };

    // ADMISSION-SPECIFIC: Filter Logic Explanation (from admission-common.js)
    const filterLogic = {
      description: 'Admission page uses updateFilterListDataBoth from admission-common.js for complex filtering',
      keyFunctions: {
        updateFilterListDataBoth: 'Main filter processing function that applies all card filters',
        priorityFilter: 'Handles priority-based filtering where first filter affects percentage calculations',
        doctorData: 'Processes admissions by doctor',
        daysData: 'Processes admissions by day of week',
        dxData: 'Processes admissions by diagnosis',
        insuranceData: 'Processes admissions by insurance',
        floorsData: 'Processes admissions by floor/unit',
        hospitalListData: 'Processes admissions by hospital'
      },
      filterFlow: `1. Apply transfer type filter (ADMISSION or READMISSION)
          2. Apply card filters in priority order (doctor, days, dx, insurance, floors, hospital)
          3. Process ADT filters if applicable
          4. Calculate priority filter impact on percentage base
          5. Update locked totals if applicable
          6. Process facility-wise percentages
          7. Update filter totals and percentage bases
          8. Recalculate all card percentages based on new base`,
      prioritySystem: {
        explanation: `Priority filters change percentage calculation base (from updateFilterListDataBoth):
          - First applied filter becomes the denominator for other card percentages
          - Can mix ADMISSION filters (doctor, days, dx, insurance, floors, hospital) and ADT filters in priority chain
          - Filter order matters: Each filter is applied sequentially using patientFilterData
          - Example: If "Per Doctor" filter (50 admissions) is applied first,
            then Insurance card percentages use 50 as base instead of total admissions (100)
          - ADT filters can be applied AFTER admission filters in the priority chain
          - When ADT filter is first, it filters adtFilterData first, then maps back to patientFilterData
          - This is why filtering affects percentage calculations across all cards`,
        currentPriority: cardFilter?.priorityData?.[0]?.type || null,
        currentPriorityFilterType: cardFilter?.priorityData?.[0]?.filterType || null,
        priorityFilters: cardFilter?.priorityData || [],
        filterTypes: {
          ADMISSION: 'Regular admission card filters (doctor, days, dx, insurance, floors, hospital)',
          ADT: 'ADT table filters (can be mixed with admission filters in priority chain)'
        },
        admissionFilterCount: cardFilter?.priorityData?.filter(f => f.filterType === 'ADMISSION')?.length || 0,
        adtFilterCount: cardFilter?.priorityData?.filter(f => f.filterType === 'ADT')?.length || 0
      },
      transferTypeImpact: {
        explanation: `Transfer type selection affects all calculations:
          - ADMISSION: Shows only new admissions and filters all cards accordingly
          - READMISSION: Shows only readmissions and filters all cards accordingly
          - ALL: Shows combined data
          - None: Shows all data without transfer type filtering`,
        currentType: transferType,
        impact: transferType ? `Currently filtering to ${transferType} only` : 'No transfer type filter applied'
      },
      admissionSpecificLogic: {
        bedCapacityComparison: 'Admission page compares against bed capacity by default',
        lockFilterTotalState: 'Can lock to specific total for consistent percentage calculations',
        adtFiltering: 'Can apply ADT-related filters after admission filters in priority chain',
        censusAverageComparison: 'Can compare against census average for occupancy analysis'
      }
    };

    // Card tooltip information for AI understanding
    const cardTooltips = {
      currentPage: tooltipsInfo?.admissionsPage || {},
      availableCards: Object.keys(tooltipsInfo?.admissionsPage || {}),
      cardDescriptions: Object.entries(tooltipsInfo?.admissionsPage || {}).map(([cardKey, description]) => ({
        cardKey,
        description,
        cardType: cardKey.toLowerCase().replace(/[^a-z0-9]/g, '')
      }))
    };

    // CRITICAL: Chart Data Analytics for AI
    const chartAnalytics = (() => {
      if (!chartData || !Array.isArray(chartData) || chartData.length === 0) {
        return {
          available: false,
          message: 'No chart data available. Chart data will be available when viewing chart visualizations or analyzing admission trends over time.',
          note: 'User can ask to view chart or trends to access this data'
        };
      }
      
      const analysis = generateChartAnalysis(chartData, chartAggregationType);
      
      return {
        available: true,
        aggregationType: chartAggregationType,
        currentFilter: currentChartFilter,
        analysis,
        
        // CRITICAL: Question-answering guides for AI
        questionAnsweringGuides: {
          'Which week had the highest admissions?': {
            answer: `Use analysis.peak to find the period with highest value`,
            example: `The ${analysis.peak?.label} had the highest admissions with ${analysis.peak?.value} admissions`
          },
          'Are admissions trending up or down?': {
            answer: `Use analysis.trend.direction and analysis.trend.description`,
            example: `Admissions show a ${analysis.trend?.direction} trend. ${analysis.trend?.description}`
          },
          'What happened after the peak?': {
            answer: `Use analysis.postPeakAnalysis for detailed post-peak behavior`,
            example: analysis.postPeakAnalysis?.available 
              ? `After the peak, ${analysis.postPeakAnalysis.immediateResponse.description}. ${analysis.postPeakAnalysis.analysis}`
              : 'Peak occurs at the end of the period'
          },
          'Show period changes': {
            answer: `Use analysis.periodOverPeriodChanges for all period-to-period changes`,
            example: `Period-over-period changes available for ${analysis.periodOverPeriodChanges?.length} transitions`
          },
          'What is the average?': {
            answer: `Use analysis.statistics.mean, median, and other stats`,
            example: `Average: ${analysis.statistics?.mean} admissions per ${chartAggregationType}, Median: ${analysis.statistics?.median}`
          },
          'Are there any patterns?': {
            answer: `Use analysis.patterns to identify spikes, drops, and recurring patterns`,
            example: `${analysis.patterns?.description}. Spikes found: ${analysis.patterns?.spikes?.length}, Drops: ${analysis.patterns?.drops?.length}`
          }
        },
        
        // Quick reference data
        summary: {
          totalDataPoints: chartData.length,
          dateRange: `${analysis.dateRange?.start} to ${analysis.dateRange?.end}`,
          peakValue: analysis.peak?.value,
          peakPeriod: analysis.peak?.label,
          averageValue: analysis.statistics?.mean,
          medianValue: analysis.statistics?.median,
          trendDirection: analysis.trend?.direction,
          trendStrength: analysis.trend?.strength,
          overallChange: analysis.trend?.change,
          overallChangePercentage: analysis.trend?.changePercentage,
          largestIncrease: analysis.largestIncrease 
            ? `${analysis.largestIncrease.description}`
            : 'N/A',
          largestDecrease: analysis.largestDecrease 
            ? `${analysis.largestDecrease.description}`
            : 'N/A',
          volatility: analysis.summary?.volatility,
          dataQuality: analysis.summary?.dataQuality
        }
      };
    })();

    // Raw data for detailed analysis (limited for performance)
    const rawData = {
      dbData: dbData ? {
        total: dbData.total, // Total admissions (all)
        totalAdmissions: dbData.totalAdmissions, // New admissions only
        totalReAdmissions: dbData.totalReAdmissions, // Readmissions only
        bedCapacity: dbData.bedCapacity, // CRITICAL: Bed capacity is the default percentage baseline for Admission page
        censusAverage: dbData.censusAverage,
        // CRITICAL: Include raw records for cross-filtering (from patientList.list)
        // Increased limit to 1000 for accurate cross-filtering while maintaining reasonable performance
        data: Array.isArray(patientList?.list) ? patientList.list.slice(0, 1000) : []
      } : null,
      sampleFilterData: filterListData ? {
        // Include main card data (first 8 entries, limited to 10 items each)
        ...Object.fromEntries(
          Object.entries(filterListData).slice(0, 8).map(([key, value]) => [
            key, Array.isArray(value) ? value.slice(0, 10) : value
          ])
        ),
        // CRITICAL: Explicitly include averageStayDaysData for AI
        averageStayDaysData: filterListData.averageStayDaysData || null
      } : null,
      // CRITICAL: Patient data for AI analysis
      patientData: (() => {
        if (!patientList?.list || !Array.isArray(patientList.list) || patientList.list.length === 0) {
          return null;
        }
        
        
        return {
          totalPatients: patientList.list.length,
          // CRITICAL: Pass ALL fields from patient list
          patients: patientList.list.map(patient => {
            const { __v, ...patientData } = patient;
            return {
              ...patientData,
              id: patient._id
            };
          })
        };
      })(),
      // ADT data
      adtData: patientList?.adtList ? {
        available: true,
        count: patientList.adtList.length
      } : null,
      // CRITICAL: Current dashboard filters for AI cross-filter matching
      // This allows the backend to detect when AI query filters match the dashboard's current state
      // and use precomputed cardData for accurate counts (fixes AI vs dashboard count discrepancy)
      currentFilters: (() => {
        const currentFilters = {};
        const priorityData = cardFilter?.priorityData || [];

        // Map priority filter types to standardized filter keys used by AI
        priorityData.forEach(pf => {
          const filterType = pf.type;
          const selectedValues = pf.selectedValues || [];

          // Map filter types to standardized keys
          const typeMapping = {
            'dxData': 'diagnosis',
            'doctorData': 'doctor',
            'floorsData': 'floor',
            'insuranceData': 'insurance',
            'daysData': 'day_of_week',
            'hospitalData': 'hospital'
          };

          const filterKey = typeMapping[filterType] || filterType;

          if (selectedValues.length > 0) {
            // Extract labels from selected values for comparison
            currentFilters[filterKey] = selectedValues.map(v =>
              typeof v === 'object' ? (v.label || v.name || String(v)) : String(v)
            );
          }
        });

        return currentFilters;
      })()
    };

    // CRITICAL: Data Validation Rules for AI
    const dataValidationRules = {
      forExactCounts: {
        rule: "ALWAYS use item.actualValue or item.total for 'how many' questions",
        wrongFields: ["scaledValue", "projectedValue", "scaledPercentage"],
        correctFields: ["actualValue", "total", "value"],
        example: "For 'How many admissions by insurance?', use insuranceData[0].actualValue, NOT scaledValue"
      },
      forPercentages: {
        rule: "Use calculatedPercentage or scaledPercentage for percentage questions",
        correctFields: ["calculatedPercentage", "scaledPercentage", "percentage"],
        admissionSpecific: "Percentages often compared against bed capacity"
      },
      forProjections: {
        rule: "Use projectedValue only for projection/forecast questions",
        correctFields: ["projectedValue", "projectedPercentage"]
      },
      commonMistakes: {
        mistake1: "Using scaledValue instead of actualValue for counts",
        mistake2: "Applying rounding to base counts",
        mistake3: "Using projected values for current data questions",
        mistake4: "Confusing admissions with readmissions"
      }
    };

    // CRITICAL: Common User Questions and How AI Should Respond
    const commonQuestions = {
      "How many admissions by doctor?": {
        answer: "Find doctorData in cardData, use item.actualValue or item.total",
        calculation: "Use item.actualValue - this is the EXACT count, no rounding"
      },
      "How many readmissions?": {
        answer: "Look at dbData.totalReAdmissions for total readmissions",
        calculation: "Use dbData.totalReAdmissions count"
      },
      "What's the readmission rate?": {
        answer: "Calculate (readmissions / total admissions) * 100",
        calculation: "(dbData.totalReAdmissions / dbData.total) * 100"
      },
      "How many admissions to specific floor?": {
        answer: "Look at floorsData in cardData, filter by floor name",
        calculation: "Find floor in floorsData array, use item.actualValue"
      },
      "Why did percentages change when I filtered?": {
        answer: "Priority filter system changes percentage denominators",
        explanation: "First filter becomes new base for other card percentages"
      },
      "How are we doing against bed capacity?": {
        answer: "Compare total admissions to bed capacity",
        calculation: "Use dbData.total vs dbData.bedCapacity, calculate occupancy rate"
      },
      "What trends should I be concerned about?": {
        answer: "Look for high readmission rates, unusual admission patterns by day/doctor, capacity issues",
        analysis: "Use contextual anomaly detection considering expected healthcare patterns"
      }
    };

    // Core metrics (ADMISSION PAGE SPECIFIC)
    const metrics = {
      // CRITICAL: Use ADMISSION-SPECIFIC field names (not Hospital page "transfers" terminology)
      totalAdmissions, // Total admissions (new + readmissions)
      newAdmissions: admissionCount, // New admissions only (preferred naming)
      readmissions: readmissionCount, // Readmissions only (preferred naming)
      admissionCount, // New admissions only (legacy compatibility)
      readmissionCount, // Readmissions only (legacy compatibility)
      newAdmissionPercentage: totalAdmissions > 0 ? ((admissionCount / totalAdmissions) * 100).toFixed(1) : 0, // New admission %
      readmissionPercentage: totalAdmissions > 0 ? ((readmissionCount / totalAdmissions) * 100).toFixed(1) : 0, // Readmission rate (KEY QUALITY INDICATOR)
      admissionPercentage: totalAdmissions > 0 ? ((admissionCount / totalAdmissions) * 100).toFixed(1) : 0, // Legacy compatibility
      filterTotal,
      percentageAgainst,
      projectionDays,
      lastADTDate,
      bedCapacity: dbData?.bedCapacity, // CRITICAL: Default percentage baseline for Admission page
      censusAverage: dbData?.censusAverage,
      occupancyRate: dbData?.bedCapacity > 0 ? ((dbData?.censusAverage / dbData?.bedCapacity) * 100).toFixed(1) : 0
    };
      
    // Enhanced context with structured data
    return {
      pageType: PAGE_TYPE.ADMISSION,
      pageData: {
        // CRITICAL: Calculation Functions
        ...calculationContext,
        
        // CRITICAL: Card Data
        cardData: cardDataContext.cardData,
        currentPercentageBase: cardDataContext.currentPercentageBase,
        calculateCardPercentages: cardDataContext.calculateCardPercentages,
        
        // CRITICAL: Facility Analysis
        ...facilityContext,
        
        // Active facilities
        activeFacilities,
        
        // CRITICAL: Chart and Patient Details
        ...chartAndPatientContext,

        // Core metrics
        metrics,

        // IMPORTANT: Terminology definitions
        terminology,

        // Percentage Calculation Context
        percentageContext,

        // Census Percentage Settings
        censusSettings,
        
        // Current state
        currentState,
        
        // Filter information
        filters,
        
        // CRITICAL: Priority Data for AI (shows exact filter structure)
        priorityData: cardFilter?.priorityData?.map(p => ({
          type: p.type,
          filterType: p.filterType, // 'ADMISSION' or 'ADT'
          cardTitle: p.cardTitle,
          selectedChk: p.selectedChk,
          selectedValues: p.selectedValues,
          childId: p.childId,
          cardId: p.cardId,
          question: p.question
        })) || [],
        
        // Data structure for AI analysis
        dataStructure,
        
        // Available actions
        availableActions,

        // Report Actions Context
        reportActions,

        // Transfer Actions Context
        transferActions,

        // Dialog Actions Context
        dialogActions,

        // Locked Functionality Context
        lockedFunctionality,

        // Date Filtering Context
        dateFiltering,

        // Filter Logic Explanation
        filterLogic,
        
        // Card tooltip information
        cardTooltips,
        
        // Raw data for detailed analysis
        rawData,

        // Data Validation Rules
        dataValidationRules,

        // Common User Questions
        commonQuestions,

        // CRITICAL: Card metadata
        cardMetadata: ADMISSION_CUSTOM_CARD_DATA,

        // CRITICAL: Custom/Dynamic Cards
        customCards: {
          dynamicCards: dynamicCards || [],
          customTabs: patientList?.customTabs || []
        },
        
        // CRITICAL: Custom Card Business Logic (for AI understanding)
        customCardBusinessLogic: {
          types: {
            dynamicCards: {
              description: 'Question-based custom cards that aggregate data based on specific question configurations',
              processingFunction: 'processDynamicCard',
              filteringBehavior: 'Can be included in priority filters and affect other card percentages',
              dataSource: 'Processed from patient data using question input types and validation options'
            },
            customTabs: {
              description: 'Filter-based custom tabs that combine data from multiple dashboards using operations (plus/minus)',
              processingFunction: 'getCustomTabsCards',
              filteringBehavior: 'Can be included in priority filters and affect percentage calculations',
              dataSource: 'Combines data from multiple filters across different dashboard types',
              operations: ['plus (union of patient sets)', 'minus (subtraction of patient sets)']
            }
          },
          commonCustomCardQuestions: [
            {
              question: 'How do I filter by a custom card?',
              answer: 'Custom cards work just like standard cards - click items to apply filters',
              note: 'Custom cards can be in priority filters and affect percentage bases'
            },
            {
              question: 'What data is in custom tabs?',
              answer: 'Custom tabs combine data from multiple filters using plus/minus operations',
              note: 'Check the customTabs data for breakdown by page and operation type'
            },
            {
              question: 'Can I chart custom card data?',
              answer: 'Yes, all custom cards support chart views, patient lists, and reports',
              note: 'Use the accessor (not the label) as the cardType when opening charts'
            }
          ],
          importantNotes: {
            accessor: 'Always use card accessor (e.g., "customAdmissionTab") not label when triggering actions',
            filtering: 'Custom cards integrate into priority filter system like standard cards',
            percentageImpact: 'First custom card in priority affects percentage base for other cards',
            dataAvailability: 'Custom card data is processed same as standard cards using updateFilterListDataBoth'
          }
        },

        // CRITICAL: Chart Analytics
        chartAnalytics,

        // CRITICAL: Chart interpretation instructions
        chartInterpretation: {
          description: 'How to answer chart-related questions for Admission page',
          instructions: [
            '1. Check if chartAnalytics.available is true',
            '2. Use chartAnalytics.analysis for comprehensive data',
            '3. Reference chartAnalytics.questionAnsweringGuides for common questions',
            '4. Provide specific numbers, dates, and percentages from the analysis',
            '5. Always mention the aggregation type (daily, weekly, monthly)',
            '6. For trends, use analysis.trend for direction and strength',
            '7. For peaks, use analysis.peak with specific period and value',
            '8. For changes, use analysis.periodOverPeriodChanges for detailed breakdowns',
            '9. For patterns, use analysis.patterns for spikes, drops, and cycles',
            '10. For admission-specific analysis, consider bed capacity and occupancy rates'
          ],
          commonMistakes: [
            'Not specifying which week/period when answering "highest" questions',
            'Forgetting to include percentage changes in comparisons',
            'Not mentioning the context (date range, aggregation type)',
            'Providing vague answers instead of specific numbers',
            'Confusing admissions with readmissions',
            'Not explaining what happened after significant events'
          ],
          bestPractices: [
            'Always include specific numbers and percentages',
            'Mention the time period (week of X, month of Y)',
            'Provide context about the overall trend',
            'Compare to previous periods for context',
            'Explain the significance of changes',
            'Use the exact period labels from the data',
            'Consider bed capacity when analyzing admission volumes',
            'Highlight readmission rates when relevant'
          ]
        }
      },
      facilityIds: activeFacilities,
      timestamp: new Date().toISOString()
    };
  }, [
    // Core data dependencies
    dbData,
    filterListData,
    patientList,
    activeFacilities,
    dynamicCards,
    
    // Filter and state dependencies
    filter,
    transferType,
    cardFilter,
    percentageBy,
    lockedTotalBy,
    specialComparisonSet,
    filterTotal,
    percentageAgainst,
    projectionDays,
    lastADTDate,
    
    // UI state dependencies
    admissionCards,
    rangesSet,
    isLayoutMode,
    
    // Function dependencies
    lockedTotal,
    lockedByFacility,
    isCensusTotalLocked,
    facilityPercentage,
    
    // Metadata dependencies
    ADMISSION_CUSTOM_CARD_DATA,
    tooltipsInfo,
    
    // Service dependencies
    aiCensusActionsService,
    aiReportActionsService,
    aiTransferActionsService,
    aiDialogActionsService,
    aiDateSuggestionsService,
    
    // Sub-hook dependencies
    calculationContext,
    cardDataContext.calculateCardPercentages,
    cardDataContext.cardData,
    cardDataContext.currentPercentageBase,
    chartAndPatientContext,
    facilityContext,
    
    // Chart data dependencies
    chartData,
    chartAggregationType,
    currentChartFilter,
    
    // Admission-specific dependencies
    lockFilterTotalState,
    reversedColorAdmission,
    comparingAgainstDifferentFacility
  ]);
};

export default useAIAdmissionChatContext;

